import type { FlowEditorNode, FlowEditorState, FlowNodeKind, WidgetConfigState, WidgetTab } from "./types";
import { DEFAULT_CONFIG, DEFAULT_VIEWPORT, DEFAULT_WIDGET_IMAGE, ENABLED_FLOW_NODE_KINDS, ENABLED_WIDGET_TABS, WIDGET_DEFINITIONS, flowNodePalette } from "./constants";
import { createDefaultGridLayouts, getNodeGridItem, setNodeLayout } from "./flowGridUtils";
import { getDefaultNodeSettings, getDefaultNodeText, getFlowNodeSize } from "./flowNodeDefaults";
import { getFlowViewport } from "./flowViewportUtils";
import { recenterAnchoredNodes } from "./flowAnchorUtils";
import { isEnabledWidgetTab } from "./widgetUtils";
export const createFlowNode = ({
  kind,
  position,
  label,
  text,
  color,
  config = DEFAULT_CONFIG,
}: {
  kind: FlowNodeKind;
  position: { x: number; y: number };
  label?: string;
  text?: string;
  color?: string;
  config?: WidgetConfigState;
}): FlowEditorNode => {
  const settings = getDefaultNodeSettings(kind, config);

  return {
    id: `node-${Date.now()}-${Math.round(Math.random() * 10000)}`,
    type: kind,
    position,
    data: {
      kind,
      label:
        label ||
        flowNodePalette.find((item) => item.kind === kind)?.label ||
        "Node",
      text: text || "",
      color:
        color ||
        flowNodePalette.find((item) => item.kind === kind)?.color ||
        "#168FEB",
      width: getFlowNodeSize(kind).width,
      height: getFlowNodeSize(kind).height,
      imageUrl: kind === "image" ? DEFAULT_WIDGET_IMAGE : undefined,
      layouts: createDefaultGridLayouts(kind, settings),
      settings,
    },
  };
};

export const createDefaultFlowState = (tab: WidgetTab, config: WidgetConfigState) => {
  const viewport = getFlowViewport();
  const node = createFlowNode({
    kind:
      tab === "goal"
        ? "goal"
        : tab === "leaderboard"
          ? "leaderboard"
          : tab === "alert"
            ? "alert"
            : tab === "chat"
              ? "chat"
              : "timer",
    position: { x: 120, y: 96 },
    label: WIDGET_DEFINITIONS[tab].name,
    text: getDefaultNodeText(tab, config),
    color:
      tab === "goal"
        ? config.goal.accentColor
        : tab === "alert"
          ? config.alert.accentColor
          : "#168FEB",
    config,
  });

  const centeredNode: FlowEditorNode = {
    ...node,
    data: {
      ...node.data,
      settings: {
        ...(node.data.settings || {}),
        anchor: "center",
      },
    },
  };

  return {
    nodes: [
      setNodeLayout(
        centeredNode,
        viewport,
        getNodeGridItem(centeredNode, viewport),
      ),
    ],
    edges: [],
    viewport,
  };
};

export const isFlowNodeKind = (value: unknown): value is FlowNodeKind =>
  flowNodePalette.some((item) => item.kind === value);

export const isEnabledFlowNodeKind = (value: unknown): value is FlowNodeKind =>
  isFlowNodeKind(value) && ENABLED_FLOW_NODE_KINDS.includes(value);

export const isEnabledFlowNode = (node: FlowEditorNode) =>
  isEnabledFlowNodeKind(node.data.kind || node.type);

export const constrainFlowStateToEnabledObjects = (
  state: FlowEditorState,
  tab: WidgetTab,
  config: WidgetConfigState,
): FlowEditorState => {
  const nodes = state.nodes.filter(isEnabledFlowNode);
  if (!nodes.length) {
    return createDefaultFlowState(
      isEnabledWidgetTab(tab) ? tab : "alert",
      config,
    );
  }

  const nodeIds = new Set(nodes.map((node) => node.id));

  return {
    ...state,
    nodes,
    edges: state.edges.filter(
      (edge) => nodeIds.has(edge?.source) && nodeIds.has(edge?.target),
    ),
  };
};
